/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import PropTypes from 'prop-types';
import { Camera, X, FileText, Music, Video } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import CaptureHeader from '../CaptureHeader';
import Modal from '../Modal';
import './capture.css';
import ReferenceTooltip from './ReferenceTooltip';
import { DEFAULT_CUSTOMER_REFERENCE_LABEL, resolveCustomerReferenceLabel } from '../../utils/customerReferenceLabel';
import { getMediaCategory, isBrowserNativeVideo } from '../../constants/mimeMap';
import fileAttachedPin from '../../assets/file-attached-pin.svg';

// Determine effective rendering category for an item.
// For 'file' type items, use mimeType + fileName (handles application/octet-stream fallback).
// For native types, pass through directly.
const getEffectiveCategory = (type, mimeType, fileName) => {
  if (type === 'file') return getMediaCategory(mimeType, fileName);
  return type; // 'photo' | 'video' | 'identifier'
};


const FullScreenViewer = ({ url, type, onClose, duration, mimeType, fileName }) => {
  const effectiveCategory = getEffectiveCategory(type, mimeType, fileName);
  const [videoError, setVideoError] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 40, right: 20, zIndex: 10000, background: 'white', border: 'none', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'black', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}
      >
        <X size={24} strokeWidth={2.5} />
      </button>
      {effectiveCategory === 'video' ? (
        videoError ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
            <Video size={36} className="text-base-content/30" strokeWidth={1.5} />
            <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Browser does not support this video type</p>
          </div>
        ) : (
            <>
              {videoLoading && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                  <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin" />
                </div>
              )}
              <video
                onLoadedMetadata={(e) => {
                  if (Number.isFinite(duration)) e.target.currentTime = duration;
                }}
                onDurationChange={(e) => {
                  if (Number.isFinite(e.target.duration) && Number.isFinite(duration)) {
                    e.target.currentTime = duration;
                  }
                }}
                onCanPlayThrough={(e) => { e.target.style.opacity = 1; setVideoLoading(false); }}
                src={url}
                controls
                autoPlay
                playsInline
                onClick={e => e.stopPropagation()}
                style={{ width: '100vw', height: '100vh', objectFit: 'contain', opacity: 0, transition: 'opacity 0.2s' }}
                onError={() => setVideoError(true)}
              />
            </>
        )
      ) : (effectiveCategory === 'image' || effectiveCategory === 'photo' || effectiveCategory === 'identifier') ? (
        imageError ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
            <Camera size={36} className="text-base-content/30" strokeWidth={1.5} />
            <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Browser does not support this image type</p>
          </div>
        ) : (
          <img src={url} alt="Full view" onClick={e => e.stopPropagation()} style={{ width: '100vw', height: '100vh', objectFit: 'contain' }} onError={() => setImageError(true)} />
        )
      ) : (
        <div className="text-white" onClick={e => e.stopPropagation()}>
          {effectiveCategory === 'audio' ? (
            audioError ? (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
                <Music size={36} className="text-base-content/30" strokeWidth={1.5} />
                <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Browser does not support this audio type</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <Music size={64} strokeWidth={1} />
                <audio controls preload="metadata" src={url} onError={() => setAudioError(true)} />
                <p className="text-sm opacity-60">{mimeType || fileName}</p>
              </div>
            )
          ) : effectiveCategory === 'document' ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
              <FileText size={36} className="text-base-content/30" strokeWidth={1.5} />
              <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Preview not available for this file type</p>
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
              <FileText size={36} className="text-base-content/30" strokeWidth={1.5} />
              <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Unsupported file type</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

FullScreenViewer.propTypes = {
  url: PropTypes.string,
  type: PropTypes.string,
  mimeType: PropTypes.string,
  fileName: PropTypes.string,
  onClose: PropTypes.func,
  duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const MediaCard = ({ url, type, duration, mimeType, fileName, onError }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const videoRef = useRef(null);

  const effectiveCategory = getEffectiveCategory(type, mimeType, fileName);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const handleLoad = () => { setLoading(false); };
  const handleVideoCanPlay = () => { setLoading(false); };
  const handleVideoError = () => { setError(true); setLoading(false); onError?.(); };
  const handleImageError = () => { setError(true); onError?.(); };

  // For iOS: Force load on mount
  useEffect(() => {
    if (effectiveCategory === 'video' && videoRef.current) {
      videoRef.current.load();
    }
  }, [effectiveCategory, url]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // Document — always show extension placeholder, no preview, no error
  if (effectiveCategory === 'document') {
    return (
      <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
        <img
          src={fileAttachedPin}
          alt="attachment"
          className="w-[42px] h-[42px] shrink-0 theme-invert-icon"
        />
        <span className="text-[18px] md:text-[20px] text-base-content truncate">
          {fileName || 'file'}
        </span>
      </div>
    );
  }

  // Non-native video format (e.g. FLV, WMV, AVI, MKV) — always show placeholder directly
  if (effectiveCategory === 'video' && !isBrowserNativeVideo(mimeType, fileName)) {
    return (
      <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
        <img
          src={fileAttachedPin}
          alt="attachment"
          className="w-[42px] h-[42px] shrink-0 theme-invert-icon"
        />
        <span className="text-[18px] md:text-[20px] text-base-content truncate">
          {fileName || 'file'}
        </span>
      </div>
    );
  }

  // Audio — attempt to render, show error only if browser can't play it
  if (effectiveCategory === 'audio') {
    if (error) {
      return (
        <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
          <img
            src={fileAttachedPin}
            alt="attachment"
            className="w-[42px] h-[42px] shrink-0 theme-invert-icon"
          />
          <span className="text-[18px] md:text-[20px] text-base-content truncate">
            {fileName || 'file'}
          </span>
        </div>
      );
    }
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
        <audio
          controls
          preload="metadata"
          src={url}
          onError={() => { setError(true); onError?.(); }}
        />
      </div>
    );
  }

  return (
    <>
      {loading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-base-200">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {effectiveCategory === 'video' ? (
        error ? (
          <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
            <img
              src={fileAttachedPin}
              alt="attachment"
              className="w-[42px] h-[42px] shrink-0 theme-invert-icon"
            />
            <span className="text-[18px] md:text-[20px] text-base-content truncate">
              {fileName || 'file'}
            </span>
          </div>
        ) : (
          <video
            ref={videoRef}
            src={url}
            className="w-full h-full object-contain"
            onCanPlay={handleVideoCanPlay}
            onLoadedData={handleVideoCanPlay}
            onError={handleVideoError}
            muted
            playsInline
            preload="auto"
            poster=""
            style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.3s' }}
          />
        )
      ) : (
        error ? (
          <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
            <img
              src={fileAttachedPin}
              alt="attachment"
              className="w-[42px] h-[42px] shrink-0 theme-invert-icon"
            />
            <span className="text-[18px] md:text-[20px] text-base-content truncate">
              {fileName || 'file'}
            </span>
          </div>
        ) : (
          <img
            src={url}
            alt="capture"
            className="w-full h-full object-contain mx-auto"
            onLoad={handleLoad}
            onError={handleImageError}
            style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.3s' }}
          />
        )
      )}
    </>
  );
};

MediaCard.propTypes = {
  url: PropTypes.string,
  type: PropTypes.string,
  mimeType: PropTypes.string,
  fileName: PropTypes.string,
  onError: PropTypes.func,
  duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const ReviewItem = ({ item, index, capturedFiles, serverCompleted, completedItemsMedia, onRetake, onDelete, setViewer, setDeleteModal, downloadingItems }) => {
  const isCaptured = !!(capturedFiles[item.assignmentItemId] || serverCompleted[item.assignmentItemId]);
  const isSkipped = item.status === 'skipped';
  const media = completedItemsMedia[item.assignmentItemId] || capturedFiles[item.assignmentItemId];
  const url = media?.url || media?.previewUrl;
  const duration = media?.duration || 0;
  const mimeType = media?.mimeType || media?.file?.type || null;
  const fileName = media?.fileName || media?.file?.name || null;
  const effectiveCategory = getEffectiveCategory(item.type, mimeType, fileName);
  const [mediaError, setMediaError] = useState(false);
  const isNativeVideo = effectiveCategory === 'video' && isBrowserNativeVideo(mimeType, fileName);
  const canPreview = (
    effectiveCategory === 'photo' ||
    effectiveCategory === 'identifier' ||
    effectiveCategory === 'image' ||
    isNativeVideo
  ) && !mediaError;

  return (
    <div className="animate-fade-in">
      {/* Step Label */}
      <div className="flex flex-wrap items-center justify-between mb-3">
        <div className="flex items-start gap-2 custom-line-break">
          <span className="text-[14px] font-bold text-base-content whitespace-nowrap">Step {index + 1}</span>
          <span className="text-[14px] text-base-content custom-line-break flex-1">
            {item.description}
            {item.required ? <span className="text-error ml-1 font-bold">*</span> : (<span><span className="ml-1"> (Optional)</span></span>)}
          </span>
        </div>
        {item.type === 'identifier' && (media?.customerIdentifierText ?? media?.identifierText) && (
          <div className="bg-primary/10 border-primary/20 border rounded-xl text-primary px-2 py-1 text-xs font-medium text-primary-700 inset-ring inset-ring-primary-600/10">
            <span className="text-[12px] font-semibold uppercase custom-line-break">{media.customerIdentifierText ?? media.identifierText}</span>
          </div>
        )}
      </div>

      <div className="relative rounded-[8px] overflow-hidden bg-black aspect-video border border-base-300 shadow-md">
        {url ? (
          <div className="w-full h-full relative">
            <div
              className="w-full h-full cursor-pointer group/media"
              onClick={(e) => {
                if (canPreview) {
                  setViewer({ url, type: item.type, duration, mimeType, fileName });
                }
              }}
            >
              <MediaCard key={url} url={url} type={item.type} duration={duration} mimeType={mimeType} fileName={fileName} onError={() => setMediaError(true)} />
              {canPreview && (
                <div className="absolute inset-0 bg-black/5 flex items-center justify-center">
                  <div className="bg-primary/90 backdrop-blur-md px-4 py-2 rounded-full text-primary-content font-black text-[10px] uppercase tracking-widest shadow-lg">Tap to preview</div>
                </div>
              )}
            </div>
          </div>
        ) : isSkipped ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center p-8 bg-base-200/50">
            <div className="w-12 h-12 rounded-full bg-base-content/5 flex items-center justify-center text-base-content/30">
              <Camera size={24} strokeWidth={1.5} />
            </div>
            <p className="text-[12px] font-black text-base-content/40 uppercase tracking-widest">Step Skipped</p>
            <button onClick={() => onRetake(item.assignmentItemId)} className="bg-primary text-primary-content text-[12px] font-black px-6 py-2.5 rounded-full uppercase tracking-widest shadow-lg active:scale-95 transition-transform">{item.type === 'file' ? "Upload Now" : "Capture Now"}</button>
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center p-8 bg-base-200">
            <Camera size={32} strokeWidth={1.5} className="text-base-content/30" />
            <p className="text-[12px] font-black text-base-content/40 uppercase tracking-widest">No Capture Yet</p>
            <button onClick={() => onRetake(item.assignmentItemId)} className="bg-primary text-primary-content text-[12px] font-black px-6 py-2.5 rounded-full uppercase tracking-widest shadow-lg">Capture Now</button>
          </div>
        )}
      </div>

      {isCaptured && (
        <div className="mt-3">
          <div className="flex justify-center gap-6">
            <button
              onClick={() => onRetake(item.assignmentItemId)}
              className="w-[100px] h-[40px] bg-neutral text-neutral-content font-semibold text-[20px] rounded-[8px] active:scale-[0.98] transition-all shadow-md flex items-center justify-center whitespace-nowrap"
            >
              {item.type === 'file' ? 'Replace' : 'Retake'}
            </button>
            {!item.required && <button
              onClick={() => setDeleteModal({ isOpen: true, itemId: item.assignmentItemId, itemDescription: item.description })}
              className="w-[100px] h-[40px] bg-error text-error-content font-semibold text-[20px] rounded-[8px] active:scale-[0.98] transition-all shadow-md flex items-center justify-center whitespace-nowrap"
            >
              Delete
            </button>}
          </div>
        </div>
      )}
    </div>
  );
};

ReviewItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    assignmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    description: PropTypes.string,
    required: PropTypes.bool,
    status: PropTypes.string,
  }),
  index: PropTypes.number,
  capturedFiles: PropTypes.object,
  serverCompleted: PropTypes.object,
  completedItemsMedia: PropTypes.object,
  onRetake: PropTypes.func,
  onDelete: PropTypes.func,
  setViewer: PropTypes.func,
  setDeleteModal: PropTypes.func,
  downloadingItems: PropTypes.object,
};

const Review = ({
  items,
  capturedFiles,
  serverCompleted,
  completedItemsMedia,
  branding,
  workflow,
  assignment,
  submitError,
  onRetake,
  onDelete,
  onSubmit,
  onStart,
}) => {
  const [viewer, setViewer] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const tooltipTriggerRef = useRef(null);
  const nameTooltipTriggerRef = useRef(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, itemId: null, itemDescription: '' });
  const [downloadingItems] = useState({});

  const currentTheme = branding.theme || 'light';
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);

  // Scroll to top when a submit error appears so the user sees the error banner immediately
  useEffect(() => {
    if (submitError) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [submitError]);

  const requiredItems = items.filter(i => i.required);
  const allRequiredCaptured = requiredItems.every(
    i => serverCompleted[i.assignmentItemId] || capturedFiles[i.assignmentItemId]
  );

  return (
    <div data-theme={currentTheme} className="min-h-screen flex flex-col bg-base-100 text-base-content overflow-x-hidden capture-container">
      {viewer && <FullScreenViewer url={viewer.url} type={viewer.type} onClose={() => setViewer(null)} duration={Number(viewer.duration)} mimeType={viewer.mimeType} fileName={viewer.fileName} />}

      {/* Notch Spacer */}
      <div className="h-[env(safe-area-inset-top,20px)] bg-base-100" />

      {/* Header */}
      <div className="w-full bg-base-100/95 backdrop-blur-sm p-3 pt-0 sticky top-0 z-30">
        <div className="max-w-lg mx-auto w-full">
          {/* CaptureHeader Integration */}
          <CaptureHeader expiresAt={assignment?.expiresAt} brandLogo={workflow.branding?.logoUrl} branding={branding} className="pt-0 pb-0" type='after-capture' />

          <div className="flex flex-col gap-1 mt-1">
            <div className="relative min-w-0">
              <h2
                ref={nameTooltipTriggerRef}
                className="text-[20px] font-bold text-base-content leading-none tracking-tight truncate cursor-pointer transition-opacity hover:opacity-80 active:opacity-70"
                onClick={() => setShowNameTooltip(!showNameTooltip)}
              >
                {assignment.submitterName || assignment.workflowName || 'Mobile Capture'}
              </h2>
              <ReferenceTooltip
                assignment={assignment}
                workflow={workflow}
                show={showNameTooltip}
                onClose={() => setShowNameTooltip(false)}
                theme={currentTheme}
                triggerRef={nameTooltipTriggerRef}
                type="name"
              />
            </div>
            <div className="shrink-0 overflow-visible text-[14px]">
              <div className="relative flex justify-between items-start custom-line-break w-full">
                <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:{" "}</span>
                <span className="inline-block text-right shrink-0">
                  <button
                    type="button"
                    ref={tooltipTriggerRef}
                    onClick={() => setShowTooltip(!showTooltip)}
                    className="focus:outline-none underline decoration-dashed text-primary custom-line-break"
                  >
                    {assignment.customerReferenceNumber}
                  </button>
                </span>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showTooltip}
                  onClose={() => setShowTooltip(false)}
                  theme={currentTheme}
                  triggerRef={tooltipTriggerRef}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 p-3 space-y-5 overflow-y-auto w-full max-w-lg mx-auto pb-[120px]">
        <div>
          <h1 className="text-[20px] font-bold text-base-content mb-2 tracking-tight">Check your media before submitting</h1>
          <p className="text-[14px] leading-tight text-base-content">
            Review each item below. You can retake or remove any capture before submitting your media.
          </p>
        </div>

        {submitError && (
          <div className="p-4 bg-error/10 border border-error/20 rounded-xl text-[13px] font-bold text-error flex items-center gap-3">
            <div className="w-2 h-2 bg-error rounded-full shrink-0" />
            {submitError}
          </div>
        )}

        <div className="space-y-5">
          {items.map((item, index) => (
            <ReviewItem
              key={item.id}
              item={item}
              index={index}
              capturedFiles={capturedFiles}
              serverCompleted={serverCompleted}
              completedItemsMedia={completedItemsMedia}
              onRetake={onRetake}
              onDelete={onDelete}
              setViewer={setViewer}
              setDeleteModal={setDeleteModal}
              downloadingItems={downloadingItems}
            />
          ))}
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 p-6 px-3 bg-base-100/95 backdrop-blur-sm rounded-t-[20px] z-30 shadow-[0_-4px_10px_rgba(0,0,0,0.1)] border-t border-base-300/50">
        <div className="max-w-lg mx-auto flex flex-col gap-3">
          <button
            onClick={onSubmit}
            disabled={!allRequiredCaptured}
            className={`w-full py-2 rounded-xl font-semibold text-[20px] transition-all active:scale-[0.98] shadow-xl ${allRequiredCaptured ? 'bg-primary text-primary-content' : 'bg-base-200 text-base-content/30 cursor-not-allowed'}`}
          >
            Send Media
          </button>
          {/* <button
            onClick={onStart}
            className="flex items-center justify-center gap-2 text-base-content/40 font-bold text-[15px] hover:text-base-content py-1 leading-none transition-colors"
          >
            <ChevronLeft size={20} strokeWidth={2.5} />
            Back to steps
          </button> */}
        </div>
      </div>

      {/* Extra spacing for home indicator */}
      <div className="h-[env(safe-area-inset-bottom,20px)] bg-base-100" />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, itemId: null, itemDescription: '' })}
        title="Delete Media"
        fontFamily="Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', Oxygen, sans-serif"
        theme={currentTheme}
      >
        <div className="space-y-4">
          <p className="text-base-content/60 text-[15px]">
            Are you sure you want to delete this media?
          </p>
          <p className="text-base-content font-semibold text-[14px]">
            {deleteModal.itemDescription}
          </p>
          <p className="text-base-content/50 text-[13px]">
            This action cannot be undone.
          </p>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setDeleteModal({ isOpen: false, itemId: null, itemDescription: '' })}
              className="flex-1 py-3 px-4 bg-base-200 text-base-content font-semibold text-[15px] rounded-xl hover:bg-base-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onDelete?.(deleteModal.itemId);
                setDeleteModal({ isOpen: false, itemId: null, itemDescription: '' });
              }}
              className="flex-1 py-3 px-4 bg-error text-error-content font-semibold text-[15px] rounded-xl hover:bg-error/90 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Review;

Review.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      assignmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      description: PropTypes.string,
      required: PropTypes.bool,
      status: PropTypes.string,
    })
  ),
  capturedFiles: PropTypes.object,
  serverCompleted: PropTypes.object,
  completedItemsMedia: PropTypes.object,
  branding: PropTypes.shape({
    theme: PropTypes.string,
  }),
  workflow: PropTypes.shape({
    branding: PropTypes.shape({
      logoUrl: PropTypes.string,
    }),
  }),
  assignment: PropTypes.shape({
    expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    workflowName: PropTypes.string,
    customerReferenceNumber: PropTypes.string,
  }),
  submitError: PropTypes.string,
  onRetake: PropTypes.func,
  onDelete: PropTypes.func,
  onSubmit: PropTypes.func,
  onStart: PropTypes.func,
};